/**
 * Feature Rows — alternierende Icon-Feature-Zeilen.
 *
 * Zickzack-Layout zum Erklären: jede Zeile hat eine große Icon-Disc und einen
 * Text-Block (Titel + Caption), abwechselnd links/rechts ausgerichtet. Die
 * Zeilen gleiten von ihrer jeweiligen Seite ein — angenehm zu lesen, ideal um
 * 3–4 Kernfunktionen nacheinander zu erklären.
 *
 * Items-Format:  "Titel | Caption | iconKey"
 *   z. B. "Lokal kaufen | Anbieter direkt aus deiner Stadt | store"
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, gsapSetLine,
  makeTl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";
import { lucideIcon, splitItem } from "../visual/_mockHelpers.js";

export const featureRows: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const violet = themeColors.secondary;
  const pink = themeColors.accent;
  const palette = [accent, violet, pink, accent];
  const isVertical = ctx.format === "9:16";

  const eyebrow = inputs.extras?.eyebrow != null ? String(inputs.extras.eyebrow) : "";
  const headline = inputs.text ?? "";

  const items = (inputs.items?.length
    ? inputs.items
    : [
        "Lokal kaufen | Anbieter direkt aus deiner Stadt | store",
        "Alles in einer App | Essen, Shops, Services, Events | globe",
        "Fair & transparent | 0 % Provision oder Flatrate | euro",
        "Sicher & verifiziert | Echte Anbieter, Käuferschutz | shield",
      ]
  ).slice(0, 4);

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const eyeId = `${clipPrefix}-eye`;
  const headId = `${clipPrefix}-head`;

  const parsed = items.map((raw, i) => {
    const [title, caption, iconRaw] = splitItem(raw);
    return {
      title: (title ?? "").trim(),
      caption: (caption ?? "").trim(),
      icon: (iconRaw ?? "").trim() || "star",
      color: palette[i % palette.length] ?? accent,
    };
  });

  const disc = isVertical ? 116 : 104;
  const titlePx = isVertical ? md * 0.74 : md * 0.64;
  const capPx = isVertical ? sm * 0.64 : sm * 0.58;

  const rowIds = parsed.map((_, i) => `${clipPrefix}-row-${i}`);
  const discIds = parsed.map((_, i) => `${clipPrefix}-disc-${i}`);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "allyver", accent });

  function renderRow(i: number, it: (typeof parsed)[number]): string {
    const color = it.color;
    const reverse = i % 2 === 1;
    const discHtml = `<div id="${discIds[i]}" style="flex-shrink:0;width:${disc}px;height:${disc}px;border-radius:30px;display:flex;align-items:center;justify-content:center;background:radial-gradient(circle at 30% 26%, ${withAlpha(color, 0.5)} 0%, ${withAlpha(color, 0.14)} 65%, ${withAlpha(themeColors.bg, 0.85)} 100%);border:1px solid ${withAlpha(color, 0.55)};box-shadow:0 0 34px ${withAlpha(color, 0.45)};color:${themeColors.fg};">${lucideIcon(it.icon, Math.round(disc * 0.48), themeColors.fg, 2.1)}</div>`;
    const textHtml = `<div style="display:flex;flex-direction:column;gap:6px;text-align:${reverse ? "right" : "left"};${reverse ? "align-items:flex-end;" : ""}">
      <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${titlePx}px;line-height:1.04;letter-spacing:-1px;color:${themeColors.fg};text-wrap:balance;">${escapeHtml(it.title)}</div>
      ${it.caption ? `<div style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${capPx}px;letter-spacing:0.1px;line-height:1.3;color:${withAlpha(themeColors.fg, 0.72)};max-width:${isVertical ? 620 : 720}px;text-wrap:balance;">${escapeHtml(it.caption)}</div>` : ""}
    </div>`;
    return `<div id="${rowIds[i]}" style="display:flex;align-items:center;gap:${isVertical ? 30 : 36}px;${reverse ? "flex-direction:row-reverse;" : ""}justify-content:center;will-change:transform,opacity;">
      ${discHtml}${textHtml}
    </div>`;
  }

  const rowsHtml = parsed.map((it, i) => renderRow(i, it)).join("");
  const eyePx = sm * 0.6;
  const headPx = isVertical ? lg * 0.84 : lg * 0.76;
  const hasHeader = Boolean(headline || eyebrow);

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:stretch;justify-content:center;gap:${isVertical ? 34 : 30}px;padding:${isVertical ? "120px 90px" : "104px 150px"};overflow:hidden;">
    ${hasHeader ? `<div style="display:flex;flex-direction:column;gap:12px;align-items:center;text-align:center;max-width:${isVertical ? 940 : 1300}px;margin:0 auto;">
      ${eyebrow ? `<div id="${eyeId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyePx}px;letter-spacing:6px;text-transform:uppercase;color:${accent};display:inline-flex;align-items:center;gap:14px;"><span style="width:34px;height:2px;background:${accent};"></span><span>${escapeHtml(eyebrow)}</span></div>` : ""}
      ${headline ? `<div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headPx}px;line-height:1.04;letter-spacing:-1.8px;color:${themeColors.fg};text-wrap:balance;">${escapeHtml(headline)}</div>` : ""}
    </div>` : ""}
    <div style="display:flex;flex-direction:column;gap:${isVertical ? 24 : 22}px;width:100%;max-width:${isVertical ? 980 : 1280}px;margin:0 auto;">${rowsHtml}</div>
  </div>`;

  const init = [
    eyebrow ? gsapSetLine(`#${eyeId}`, { opacity: 0, y: -8 }) : "",
    headline ? gsapSetLine(`#${headId}`, { opacity: 0, y: 22 }) : "",
    ...rowIds.map((id, i) => gsapSetLine(`#${id}`, { opacity: 0, x: i % 2 === 1 ? 60 : -60 })),
    ...discIds.map((id) => gsapSetLine(`#${id}`, { scale: 0, transformOrigin: "50% 50%" })),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [];
  if (eyebrow) tweens.push(t.to(`#${eyeId}`, { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" }, 0.1));
  if (headline) tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "power3.out" }, 0.2));

  const start = hasHeader ? 0.55 : 0.25;
  const revealWindow = Math.min(2.2, Math.max(1.0, clipDurationSec * 0.55));
  const step = revealWindow / Math.max(parsed.length, 1);

  parsed.forEach((_, i) => {
    const at = start + i * step;
    tweens.push(t.to(`#${rowIds[i]}`, { opacity: 1, x: 0, duration: 0.5, ease: "power3.out" }, at));
    tweens.push(t.to(`#${discIds[i]}`, { scale: 1, duration: 0.5, ease: "back.out(2.0)" }, at + 0.08));
  });

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
